import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash-es/get';

import { getDictionary } from 'app/store/data/formsConfig';
import { RootState } from 'app/store/rootReducer';
import { getFormattedDate } from 'app/services/dates.service';
import {
  DictionariesEnum,
  FieldsEnum,
  FormsEnum,
  InsurersEnum,
  LabelEnum,
  TerminationReasonEnum,
  VehicleTypeEnum,
} from 'app/shared/enums';
import { TerminationFormInterface } from 'app/shared/interfaces/TerminationForm.interface';
import { Dictionary, DictionaryEntry } from 'app/services/dict.service';
import { useDictionary } from 'app/shared/hooks/useDictionary';
import { InsurerNameEnum } from 'app/shared/enums/InsurerName.enum';

const formName = FormsEnum.TerminationGeneratorForm;

export const useSetupTerminationPreviewData = (
  formValues: TerminationFormInterface
) => {
  const [carData, setCarData] = useState('');
  const [carRegistrationNumber, setCarRegistrationNumber] = useState('');
  const [cityAndDate, setCityAndDate] = useState('');
  const [previousInsurerName, setPreviousInsurerName] = useState('');
  const [previousInsurerStreet, setPreviousInsurerStreet] = useState('');
  const [previousInsurerCity, setPreviousInsurerCity] = useState('');
  const [fullAddress, setFullAddress] = useState('');
  const [cityAndZipCode, setZipCodeAndCity] = useState('');
  const [fullName, setFullName] = useState('');
  const [terminationReason, setTerminationReason] = useState('');
  const [previousPolicyNumber, setPreviousPolicyNumber] = useState('');

  const carMakeDictionary = useSelector((state: RootState) =>
    getDictionary(state, formName, 'car.make_dictionary')
  );

  const carModelDictionary = useSelector((state: RootState) =>
    getDictionary(state, formName, 'car.model_dictionary')
  );

  const motorcycleModelDictionary = useSelector((state: RootState) =>
    getDictionary(state, formName, 'car.versionEt_dictionary')
  );

  const insurersDictionary = useSelector((state: RootState) =>
    getDictionary(state, formName, 'insurerName_dictionary')
  );

  const insurerAddressDictionary = useDictionary(
    DictionariesEnum.CarInsurersWithAddress
  );

  const getFormValue = (questionKey: FieldsEnum) =>
    get(formValues, questionKey, '');

  const getDictionaryEntryValue = (
    dictionary: Dictionary,
    formValue: string
  ) => {
    const dictEntry = dictionary?.find(
      (entry: DictionaryEntry) => entry.key === formValue
    );

    if (!dictEntry) {
      return;
    }

    return dictEntry.value;
  };

  const getCityAndDate = () => {
    const city = getFormValue(FieldsEnum.TerminationCity);
    const date = getFormattedDate();

    const value = city ? `${city}, ${date}` : '';

    setCityAndDate(value);
  };

  const getInsurerName = () => {
    const insurerKey = getFormValue(FieldsEnum.TerminationInsurerName);

    if (insurerKey === InsurersEnum.Wiener) {
      return InsurerNameEnum.Wiener;
    }

    return getDictionaryEntryValue(insurersDictionary, insurerKey) ?? '';
  };

  const getPreviousInsurerName = () => {
    const previousInsurerKey = getFormValue(
      FieldsEnum.TerminationPreviousInsurerName
    );

    const value =
      previousInsurerKey === InsurersEnum.Wiener
        ? InsurerNameEnum.Wiener
        : getDictionaryEntryValue(insurersDictionary, previousInsurerKey) ?? '';

    setPreviousInsurerName(value);
  };

  const getPreviousInsurerAddress = () => {
    const previousInsurerKey = getFormValue(
      FieldsEnum.TerminationPreviousInsurerName
    );

    const previousInsurerAddress = getDictionaryEntryValue(
      insurerAddressDictionary,
      previousInsurerKey
    );

    if (!previousInsurerAddress) {
      return;
    }

    const splittedData = previousInsurerAddress.split('<br>');
    const insurerAddress = splittedData[splittedData.length - 1].split(',');
    const insurerStreet = insurerAddress[0] ?? '';
    const insurerCity = insurerAddress[1] ?? '';

    setPreviousInsurerStreet(insurerStreet);
    setPreviousInsurerCity(insurerCity);
  };

  const getCarData = () => {
    const isMotorcycle =
      getFormValue(FieldsEnum.VehicleType) === VehicleTypeEnum.Motorcycle;
    const carMakeKey = getFormValue(FieldsEnum.CarMake);
    const carModelKey = isMotorcycle
      ? getFormValue(FieldsEnum.CarVersionEt)
      : getFormValue(FieldsEnum.CarModel);

    if (!carMakeKey || !carModelKey) {
      return;
    }

    const carMake = getDictionaryEntryValue(carMakeDictionary, carMakeKey);
    let carModel =
      carModelKey === 'no_car_type'
        ? ''
        : getDictionaryEntryValue(
            isMotorcycle ? motorcycleModelDictionary : carModelDictionary,
            carModelKey
          );

    if (isMotorcycle) {
      carModel = carModel?.split(',')[0];
    }

    const value = carMake ? `${carMake}${carModel ? ' ' + carModel : ''}` : '';

    setCarData(value);
  };

  const getFullAddress = () => {
    const streetName = getFormValue(FieldsEnum.TerminationStreetName);
    const streetNumber = getFormValue(FieldsEnum.TerminationStreetNumber);
    const apartmentNumber = getFormValue(FieldsEnum.TerminationApartmentNumber);

    let value =
      streetName && streetNumber ? `${streetName} ${streetNumber}` : '';

    if (apartmentNumber) {
      value = `${value}/${apartmentNumber}`;
    }

    setFullAddress(value);
  };

  const getFullName = () => {
    const name = getFormValue(FieldsEnum.TerminationName);
    const lastName = getFormValue(FieldsEnum.TerminationLastName);

    const value = name && lastName ? `${name} ${lastName}` : '';

    setFullName(value);
  };

  const getZipCodeAndCity = () => {
    const city = getFormValue(FieldsEnum.TerminationCity);
    const zipCode = getFormValue(FieldsEnum.TerminationZipCode);

    const value = zipCode && city ? `${zipCode}, ${city}` : '';

    setZipCodeAndCity(value);
  };

  const getTerminationReason = () => {
    const terminationReason = getFormValue(FieldsEnum.TerminationReason);
    const startDate = getFormValue(FieldsEnum.TerminationInsurancePolicyStart);
    const insurerName = getInsurerName();
    const noticeDate = getFormValue(FieldsEnum.TerminationNoticeDate);

    let value = '';

    switch (terminationReason) {
      case TerminationReasonEnum.Article28:
        value = LabelEnum.TerminationPreviewArticle28;
        break;
      case TerminationReasonEnum.Article28a:
        if (insurerName && startDate) {
          value = `${LabelEnum.TerminationPreviewArticle28a1} ${insurerName}, ${
            LabelEnum.TerminationPreviewArticle28a2
          } ${getFormattedDate(startDate)}.`;
        }
        break;
      case TerminationReasonEnum.Article31:
        value = `${LabelEnum.TerminationPreviewArticle31} ${noticeDate
          .split('-')
          .reverse()
          .join('.')}.`;
    }

    setTerminationReason(value);
  };

  useEffect(() => {
    if (!formValues?.vehicleOwner?.legalAddress?.city) {
      return;
    }

    getCityAndDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues?.vehicleOwner?.legalAddress?.city]);

  useEffect(() => {
    if (!formValues?.car?.make || !formValues?.car?.model) {
      return;
    }

    getCarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues?.car?.make,
    formValues?.car?.model,
    formValues?.car?.versionEt,
    carMakeDictionary,
    carModelDictionary,
    motorcycleModelDictionary,
  ]);

  useEffect(() => {
    if (!formValues?.previousInsurerName) {
      return;
    }

    getPreviousInsurerName();
    getPreviousInsurerAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues?.previousInsurerName,
    insurersDictionary,
    insurerAddressDictionary,
  ]);

  useEffect(() => {
    if (!formValues?.noticeReason) {
      return;
    }

    getTerminationReason();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues?.noticeReason,
    formValues?.startDate,
    formValues?.insurerName,
    formValues?.noticeDate,
    insurersDictionary,
  ]);

  useEffect(() => {
    if (!formValues?.previousPolicyNumber) {
      return;
    }

    setPreviousPolicyNumber(formValues.previousPolicyNumber);
  }, [formValues?.previousPolicyNumber]);

  useEffect(() => {
    if (!formValues?.car?.registrationNumber) {
      return;
    }

    setCarRegistrationNumber(formValues.car.registrationNumber);
  }, [formValues?.car?.registrationNumber]);

  useEffect(() => {
    if (
      !formValues?.vehicleOwner?.name ||
      !formValues?.vehicleOwner?.lastName
    ) {
      return;
    }

    getFullName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues?.vehicleOwner?.name, formValues?.vehicleOwner?.lastName]);

  useEffect(() => {
    if (
      !formValues?.vehicleOwner?.legalAddress?.streetName ||
      !formValues?.vehicleOwner?.legalAddress?.streetNumber
    ) {
      return;
    }

    getFullAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues?.vehicleOwner?.legalAddress?.streetName,
    formValues?.vehicleOwner?.legalAddress?.streetNumber,
    formValues?.vehicleOwner?.legalAddress?.apartmentNumber,
  ]);

  useEffect(() => {
    if (
      !formValues?.vehicleOwner?.legalAddress?.city ||
      !formValues?.vehicleOwner?.legalAddress?.zipCode
    ) {
      return;
    }

    getZipCodeAndCity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues?.vehicleOwner?.legalAddress?.city,
    formValues?.vehicleOwner?.legalAddress?.zipCode,
  ]);

  return {
    carData,
    carRegistrationNumber,
    cityAndDate,
    cityAndZipCode,
    fullAddress,
    fullName,
    previousInsurerName,
    previousInsurerStreet,
    previousInsurerCity,
    previousPolicyNumber,
    terminationReason,
  };
};
